import { inject, provide } from 'vue'
import type { InjectionKey, Ref } from 'vue'
import { resolveTemplateRefHtmlElement } from '~~/layers/base/utils/resolveTemplateRefElement'

/** When ref is bound to a component (e.g. UMain), value is the instance and we use .$el for the root element. */
type MainScrollRootValue = HTMLElement | { $el: HTMLElement } | null

const MAIN_SCROLL_ROOT_KEY: InjectionKey<Ref<MainScrollRootValue>> = Symbol('mainScrollRoot')

/**
 * Ref type for the main scroll root: may be the DOM element or a Vue component instance (with $el).
 * Use when passing to IntersectionObserver or reading scrollTop.
 */
export type MainScrollRootRef = Ref<MainScrollRootValue>

/**
 * Provides or injects the main layout scroll root (UMain in express layout).
 * Use this so intersection observers and scroll logic use the same scroll container as the viewport.
 *
 * **In the layout (provider):** Call with a ref and bind that ref to the scroll root (e.g. UMain).
 * The ref will receive the component instance if ref is on a component; consumers resolve to the root element when needed.
 *
 * **In descendants (consumers):** Call with no args to get the injected ref. Use the returned ref as
 * `rootRef` for IntersectionObserver; the observer composable resolves component instances to `.$el` for the Element.
 *
 * @param providerRef - When provided, this ref is provided to the tree (layout only). Bind it in template to UMain (or the scroll root).
 * @returns The same ref when providing; the injected ref when consuming (undefined if not under the layout).
 */
export function useMainScrollRoot(
    providerRef?: Ref<MainScrollRootValue>
): MainScrollRootRef | undefined {
    if (providerRef !== undefined) {
        provide(MAIN_SCROLL_ROOT_KEY, providerRef)
        return providerRef
    }
    return inject(MAIN_SCROLL_ROOT_KEY)
}

/** Resolve injected/provided scroll root ref value to a concrete HTMLElement. */
function resolveMainScrollRootElement(rootRef?: MainScrollRootRef): HTMLElement | null {
    return resolveTemplateRefHtmlElement(rootRef?.value)
}

function resolveScrollRootElement(rootRef?: MainScrollRootRef): HTMLElement | null {
    return (
        resolveMainScrollRootElement(rootRef) ??
        (typeof document !== 'undefined' ? document.getElementById('main-scroll-root') : null)
    )
}

/**
 * WKWebView (iOS) often ignores a single `scrollTop = 0` on nested overflow containers,
 * especially before layout settles. Apply the same scroll intent multiple ways and
 * on the next frame(s).
 */
function scrollElementToTop(el: HTMLElement) {
    el.scrollTop = 0
    if (typeof el.scrollTo === 'function') {
        try {
            el.scrollTo({ top: 0, left: 0, behavior: 'auto' })
        } catch {
            el.scrollTo(0, 0)
        }
    }
}

export type ScrollMainScrollRootOptions = {
    /**
     * Run extra deferred passes after images / async layout (order success, etc.).
     * iOS Capacitor commonly needs this; keep false for lightweight callers.
     */
    layoutStabilize?: boolean
}

/**
 * Scroll the main layout scroll root to top.
 * Falls back to `#main-scroll-root` then `window` when no injected/provided root is available.
 */
export function scrollMainScrollRootToTop(
    rootRef?: MainScrollRootRef,
    options?: ScrollMainScrollRootOptions
) {
    if (!import.meta.client) return

    const apply = () => {
        const root = resolveScrollRootElement(rootRef)
        if (root) {
            scrollElementToTop(root)
            return
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }

    apply()
    requestAnimationFrame(apply)
    requestAnimationFrame(() => {
        requestAnimationFrame(apply)
    })

    if (options?.layoutStabilize) {
        setTimeout(apply, 100)
        setTimeout(apply, 280)
    }
}
